import { withDependencies } from '@wix/thunderbolt-ioc'
import type { Experiments, IAppWillMountHandler, IHeadContent } from '@wix/thunderbolt-symbols'
import { ExperimentsSymbol, HeadContentSymbol } from '@wix/thunderbolt-symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

export const PREVENT_ANCHOR_RELOAD_SPEC = 'specs.thunderbolt.preventAnchorReloadBeforeHydration' as const

// Shape of `window.__tbAnchorGuard`. The registrar sets `linkClickReady` once `onLinkClick` is attached.
export type PreHydrationGuard = ((e: MouseEvent) => void) & { linkClickReady?: boolean }

// The functions below are serialized (via `.toString()`) into the inline SSR `<script>`, so they
// must stay self-contained (no module-scope references, hence the repeated selector) and keep
// their bodies comment-free.

// True for a `data-anchor` / `data-popupid` link whose href points at the current page (query +
// hash ignored). An empty href counts, since it reloads the current URL.
export function isSamePageAnchor(e: MouseEvent): boolean {
	const target = e.target as HTMLElement | null
	const anchor = target && target.closest && target.closest('a[data-anchor], a[data-popupid]')
	if (!anchor || anchor.getAttribute('target') === '_blank') {
		return false
	}
	const href = anchor.getAttribute('href')
	if (href === null) {
		return false
	}
	if (href === '') {
		return true
	}
	try {
		const url = new URL(href, location.href)
		return url.origin === location.origin && url.pathname === location.pathname
	} catch (_) {
		return false
	}
}

// True once React has hydrated the clicked anchor or an ancestor (rich-text anchors never get their
// own marker). Stops at an unclosed Suspense boundary comment (`<!--$-->`), since React hasn't
// hydrated that content yet. Anything unrecognised counts as not hydrated.
export function isClickedTreeHydrated(e: MouseEvent): boolean {
	const target = e.target as HTMLElement | null
	let node: Element | null = target && target.closest && target.closest('a[data-anchor], a[data-popupid]')
	while (node) {
		const keys = Object.keys(node)
		for (let i = 0; i < keys.length; i++) {
			if (keys[i].indexOf('__reactFiber$') === 0 || keys[i].indexOf('__reactInternalInstance$') === 0) {
				return true
			}
		}
		let sibling: Node | null = node.previousSibling
		let depth = 0
		while (sibling) {
			if (sibling.nodeType === 8) {
				const data = (sibling as Comment).data
				if (data === '/$') {
					depth++
				} else if (data.charAt(0) === '$') {
					if (depth === 0) {
						return false
					}
					depth--
				}
			}
			sibling = sibling.previousSibling
		}
		node = node.parentElement
	}
	return false
}

// Lets the click through only when both the anchor is hydrated and `onLinkClick` is attached.
// `appDidMount` runs a task after hydration, so hydration alone isn't enough.
export function preventSamePageAnchorReloadBeforeHydration(e: MouseEvent) {
	if (e.metaKey || e.ctrlKey) {
		return
	}
	if (!isSamePageAnchor(e)) {
		return
	}
	const serviceable =
		(preventSamePageAnchorReloadBeforeHydration as PreHydrationGuard).linkClickReady === true &&
		isClickedTreeHydrated(e)
	if (!serviceable) {
		e.preventDefault()
	}
}

// Inline `<script>` injected into the SSR `<head>`: installs the guard as a capture-phase `click`
// listener on `document`, exposed as `window.__tbAnchorGuard`. Emitted plain (no CSP nonce), like
// all Thunderbolt inline scripts (see render-body.ejs).
export const buildPreventSamePageAnchorReloadScript = () =>
	`<script>;(function(){${isSamePageAnchor.toString()};${isClickedTreeHydrated.toString()};var guard=(${preventSamePageAnchorReloadBeforeHydration.toString()});guard.linkClickReady=false;window.__tbAnchorGuard=guard;document.addEventListener('click',guard,true)})();</script>`

export const preHydrationAnchorGuardFactory = (
	headContent: IHeadContent,
	experiments: Experiments
): IAppWillMountHandler => ({
	appWillMount: () => {
		if (!isExperimentOpen(experiments, PREVENT_ANCHOR_RELOAD_SPEC)) {
			return
		}
		headContent.setHead(buildPreventSamePageAnchorReloadScript())
	},
})

export const PreHydrationAnchorGuard = withDependencies(
	[HeadContentSymbol, ExperimentsSymbol] as const,
	preHydrationAnchorGuardFactory
)
